import React, { useEffect, useMemo } from "react";

import clsx from "clsx";
import { Link, useLocation } from "react-router-dom";
import { type StepWizardChildProps } from "react-step-wizard";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { gql } from "@apollo/client";
import { useAuth0 } from "@components/auth0-wrapper";
import { AdminMenu } from "@components/chrome/collapsible-sidebar";
import { useUser } from "@components/user-context";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import { logEvent } from "@internal/analytics";
import {
  type OnboardingStepType,
  type GetSteps_me_onboardingSteps,
  type OnboardingUserQuery,
  type UpdateCurrentStep,
  type UpdateCurrentStepVariables,
} from "@north-beam/nb-client-side";
import { usePrefs } from "@utils/client-side-preferences";
import { relativeToRoot } from "@utils/index";

import setupLogo from "../assets/setup-logo.svg";
import { stepIsVisible, Steps, type NavConfig, type Step } from "../steps";

import { OnboardingNavItem } from "./onboarding-nav-item";

const getLatestStep = (stepA: Step | null, stepB: Step): Step => {
  if ([stepA?.value, stepB.value].includes(Steps.COMPLETE)) {
    return Steps.getModel("complete");
  }

  const stepAIndex = stepA
    ? Steps.data.findIndex((step) => step.value === stepA.value)
    : -1;
  const stepBIndex = Steps.data.findIndex((step) => step.value === stepB.value);
  if (stepBIndex === -1) {
    throw new Error(`Corrupted step '${JSON.stringify(stepB)}' encountered!`);
  }

  if (!stepA || stepAIndex === -1 || stepAIndex < stepBIndex) {
    return stepB;
  } else {
    return stepA;
  }
};

export type OnboardingNavProps = Partial<StepWizardChildProps> & {
  canVerifyAndFinish: boolean;
  onboardingV2Steps: {
    [key in OnboardingStepType]?: GetSteps_me_onboardingSteps;
  };
};

export const OnboardingNav = ({
  currentStep,
  nextStep,
  previousStep,
  goToNamedStep,
  onboardingV2Steps,
  canVerifyAndFinish,
}: OnboardingNavProps): JSX.Element => {
  const { logout } = useAuth0();

  const { user } = useUser();
  const location = useLocation();

  const [searchParams, setSearchParams] = useQueryParams({
    step: withDefault(StringParam, ""),
    provider: withDefault(StringParam, ""),
  });

  const [updateCurrentStep] = useNorthbeamMutation<
    UpdateCurrentStep,
    UpdateCurrentStepVariables
  >(UPDATE_CURRENT_STEP_V2);

  const { data } = useNorthbeamQuery<OnboardingUserQuery>(
    ONBOARDING_USER_QUERY_V2,
  );

  const activeStep = useMemo(() => {
    const step = Steps.getModelSafe(searchParams.step, {
      strict: false,
    });
    if (step && stepIsVisible(step, user)) {
      return step;
    }
    return Steps.getModel("introduction");
  }, [location, user]);

  const lastSavedStep = useMemo(() => {
    if (data) {
      const rawStepName = data.me?.onboardingDetails?.latestStep;
      if (typeof rawStepName === "string") {
        const step = Steps.getModelSafe(rawStepName, {
          strict: false,
          onError: () => {
            logEvent(
              "During onboarding, the value received for the latest step via the API is not valid.",
              { value: rawStepName },
            );
          },
        });
        if (step && stepIsVisible(step, user)) {
          return step;
        }
        return Steps.getModel("introduction");
      }
    }
    // The step will be null until the data has loaded.
    return null;
  }, [data, user]);

  useEffect(() => {
    if (activeStep) {
      const newLatestMinorStep = getLatestStep(
        activeStep,
        lastSavedStep ?? Steps.getModel("introduction"),
      );

      updateCurrentStep({
        variables: {
          currentStep: activeStep.value,
          latestStep: newLatestMinorStep.value,
        },
      });
      if (goToNamedStep) {
        goToNamedStep(activeStep.value);
      }
    }
  }, [lastSavedStep, activeStep, updateCurrentStep, goToNamedStep]);

  if (!currentStep || !goToNamedStep || !nextStep || !previousStep) {
    throw new Error(
      "The onboarding nav was not properly injected with step wizard props!",
    );
  }

  const { useDevelopmentAnalyticsData } = usePrefs();

  return (
    <div
      className={clsx(
        "flex flex-col h-full w-[290px] min-w-[320px] bg-cosmic-3",
        "max-h-[100%] overflow-hidden",
      )}
    >
      <Link
        className="p-4 w-full flex justify-center items-center"
        to={relativeToRoot("")}
      >
        <img src={setupLogo} alt="Setup Northbeam" />
      </Link>
      <div className={clsx("flex flex-col overflow-y-auto grow p-4", "mt-0")}>
        {Steps.data
          .filter((step: any) => step.navConfig && stepIsVisible(step, user))
          .map((step: any, i: number) =>
            step.navConfig && stepIsVisible(step, user) ? (
              <OnboardingNavItem
                onboardingV2Steps={onboardingV2Steps}
                key={step.value}
                disabled={step.value === Steps.COMPLETE && !canVerifyAndFinish}
                step={
                  step as Omit<Step, "navConfig"> & {
                    readonly navConfig: Exclude<NavConfig, null>;
                  }
                }
                onClick={() => {
                  setSearchParams({ step: step.value });
                  goToNamedStep(step.value);
                }}
                isActive={activeStep.value === step.value}
                stepNumber={i + 1}
                subNav={step.subNav ? step.subNav[searchParams.provider] : []}
              />
            ) : (
              <React.Fragment key={step.value}></React.Fragment>
            ),
          )}
      </div>
      <AdminMenu
        expanded={true}
        compactDesign={true}
        useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
      />
      <div
        onClick={() =>
          logout({ logoutParams: { returnTo: window.location.origin } })
        }
        className={clsx(
          "flex items-center h-[38px] px-2 rounded-lg mx-5 mb-3 cursor-pointer",
          {
            "hover:bg-gray-1": true,
          },
        )}
      >
        <i className="fa-regular fa-door-open mr-2 text-primary"></i> Logout
      </div>
    </div>
  );
};

export const UPDATE_CURRENT_STEP_V2 = gql`
  mutation UpdateCurrentStepV2($currentStep: String!, $latestStep: String!) {
    updateCurrentStep(currentStep: $currentStep, latestStep: $latestStep) {
      currentStep
    }
  }
`;

const ONBOARDING_USER_QUERY_V2 = gql`
  query OnboardingUserQueryV2 {
    me {
      onboardingDetails {
        latestStep
      }
    }
    users {
      id
      isAdmin
      email
      companyName
    }
  }
`;
